import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent,
} from 'react';

import { cn, parseNumberOnly } from '../lib/utils';

const BASE_CLASS =
  'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent dark:bg-card px-3 py-1 text-base transition-colors hover:border-muted-foreground/70 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-invalid:border-destructive aria-invalid:hover:border-destructive aria-invalid:focus-visible:ring-destructive disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:hover:border-input read-only:bg-muted read-only:focus-visible:ring-0 read-only:hover:border-input md:text-sm';

interface InputProps extends ComponentProps<'input'> {
  hideArrows?: boolean;
  /** Skip parseNumberOnly formatting during typing — validation still runs on blur */
  skipFormat?: boolean;
  /**
   * For `type="number"`: allow the field to stay empty on blur instead of
   * restoring to `defaultValue`/`min`/`0`. Use for OPTIONAL numbers with no
   * author-specified floor (e.g. an optional panel id) where blank must mean
   * "no value", not `0`. Default false preserves the #1018 restore behavior.
   */
  allowEmpty?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      value,
      defaultValue,
      min,
      max,
      step = 1,
      onChange,
      onFocus,
      onBlur,
      disabled,
      hideArrows,
      skipFormat,
      allowEmpty,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);
    const [displayValue, setDisplayValue] = useState(String(value ?? ''));
    const minNum = min != null ? Number(min) : undefined;
    const maxNum = max != null ? Number(max) : undefined;
    const stepNum = Number(step) || 1;
    const isNumber = type === 'number';
    const showArrows = isNumber && !hideArrows;

    // Sync display from parent value
    useEffect(() => {
      if (isNumber) setDisplayValue(String(value ?? ''));
    }, [value, isNumber]);

    // Set the DOM input's value via the prototype setter and fire a bubbling
    // 'input' event so controlled parents (react-hook-form, etc.) observe the
    // change. React's synthetic onChange is bypassed by direct .value writes,
    // which is why this verbose dance is required.
    const setInputValueNatively = (el: HTMLInputElement | null, next: string) => {
      if (!el) return;
      const nativeSet = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      nativeSet?.call(el, next);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      switch (type) {
        case 'number': {
          if (skipFormat) {
            setDisplayValue(e.target.value);
            break;
          }
          const raw = e.target.value;
          if (raw === '') {
            setDisplayValue('');
            // Optional field: propagate the cleared state so the controlled
            // parent records "no value". Otherwise empty is swallowed and only
            // the blur-restore ever reaches the form.
            if (allowEmpty) onChange?.(e);
            return;
          }
          const parsed = parseNumberOnly(raw, minNum, maxNum, true, 2, false);
          const clamped = String(parsed);
          setDisplayValue(clamped);
          e.target.value = clamped;
          break;
        }
      }
      onChange?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      switch (type) {
        case 'number': {
          if (skipFormat) break;
          if (displayValue === '') {
            // Optional number with no author-specified floor: blank is a valid
            // "no value" — do NOT backfill 0 (would turn an empty panel id into
            // panelId=0, a real/wrong value). Leave it empty; the cleared
            // keystroke already pushed `undefined` to the form.
            if (allowEmpty) break;
            // #1018: Restore from the consumer's `defaultValue` (e.g. SmallApp
            // manifest default), NOT from the stale `value` prop — that prop
            // can hold an upstream-injected value that fights the user's edit.
            // Falls back to minNum then 0 when no defaultValue was provided.
            const restored = String(defaultValue ?? value ?? minNum ?? 0);
            setDisplayValue(restored);
            setInputValueNatively(innerRef.current, restored);
          } else {
            const clamped = String(parseNumberOnly(displayValue, minNum, maxNum, true, 2, true));
            if (clamped !== displayValue) {
              setDisplayValue(clamped);
              setInputValueNatively(innerRef.current, clamped);
            }
          }
          break;
        }
      }
      onBlur?.(e);
    };

    const handleStep = (direction: 1 | -1) => {
      const el = innerRef.current;
      if (!el) return;
      const current = parseFloat(el.value) || 0;
      const next = parseNumberOnly(
        String(current + direction * stepNum),
        minNum,
        maxNum,
        true,
        2,
        true,
      );
      const finalValue = typeof next === 'number' ? next : current;
      setInputValueNatively(el, String(finalValue));
    };

    const inputEl = (
      <input
        type={type}
        className={cn(
          BASE_CLASS,
          isNumber &&
            '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]',
          // When wrapped with stepper buttons, hover on the group (input OR arrows)
          // darkens the input border so the whole control reads as one thing.
          showArrows && 'pr-6 group-hover:border-muted-foreground/70',
          className,
        )}
        ref={innerRef}
        min={min}
        max={max}
        step={step}
        value={isNumber ? displayValue : value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        disabled={disabled}
        {...props}
      />
    );

    if (!showArrows) return inputEl;

    const currentNum = parseFloat(displayValue) || 0;
    const atMax = maxNum != null && currentNum >= maxNum;
    const atMin = minNum != null && currentNum <= minNum;

    return (
      <div className={cn('group relative flex w-full', className)}>
        {inputEl}
        <div className="absolute right-0 inset-y-0 flex flex-col overflow-hidden rounded-r-md border-l border-input group-hover:border-muted-foreground/70">
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || atMax}
            className="flex flex-1 cursor-pointer items-center justify-center px-1 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleStep(1)}
            aria-label="Increase"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || atMin}
            className="flex flex-1 cursor-pointer items-center justify-center px-1 border-t border-input text-muted-foreground hover:text-foreground group-hover:border-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleStep(-1)}
            aria-label="Decrease"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input, type InputProps };
